import React, { useState } from 'react';
import { Button, CircularProgress, Dialog } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Team } from '../../models/types';
import { apiService } from '../../services/api.service';
import { ContestFormDialogLayout, FormFooterButtons } from './ContestFormDialogLayout';

interface TeamDeleteDialogProps {
  team: Team | null;
  memberCount: number;
  onCancel: () => void;
  onDeleted: (team: Team) => void;
  onError: (message: string) => void;
}

// Confirms and performs a team delete; members of the team become unassigned.
export const TeamDeleteDialog = ({ team, memberCount, onCancel, onDeleted, onError }: TeamDeleteDialogProps) => {
  const { t } = useTranslation('contests');
  const [deleting, setDeleting] = useState(false);

  const close = () => !deleting && onCancel();

  const confirm = async () => {
    if (!team) return;
    try {
      setDeleting(true);
      await apiService.deleteTeam(team._id);
      onDeleted(team);
    } catch (error) {
      console.error('Error deleting team:', error);
      onError(error instanceof Error ? error.message : t('notify.teamDeleteError'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!team} onClose={close} maxWidth="xs" fullWidth aria-labelledby="delete-team-dialog-title">
      <ContestFormDialogLayout
        title={<span id="delete-team-dialog-title">{t('deleteTeamDialog.title')}</span>}
        subtitle={t('deleteTeamDialog.message', { team: team?.name ?? '', count: memberCount })}
        onClose={close}
        footer={
          <>
            <FormFooterButtons onCancel={close} hideSubmit />
            <Button
              variant="contained"
              color="error"
              onClick={confirm}
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
              {deleting ? t('common:actions.deleting') : t('deleteTeamDialog.confirm')}
            </Button>
          </>
        }
      >
        {null}
      </ContestFormDialogLayout>
    </Dialog>
  );
};
